import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useCreatePolicy } from '../api/policies';
import {
  editableRoles,
  hasPartnerRefusal,
  linksOf,
  partnerRefusalOf,
  seedPartnerValues,
  type PartnerFormValues,
} from '../model/partners';
import {
  coercePayload,
  fieldNameOfIssuePath,
  payloadFormOf,
  payloadSchemaOf,
} from '../model/payload-form';
import { PartnerSection } from './PartnerSection';
import { PayloadFields } from './PayloadFields';

/**
 * Enrolling a member under a Gruppen-Police (SDD-011 Q5).
 *
 * Everything the dialog asks for comes THROUGH THE AGREEMENT'S PIN: the schema the payload must
 * satisfy and the partner roles the policy must carry are the child version's, reached through the
 * group version (`GET /v1/group-policies/{code}/member-schema`) — never the tarif's newest. The
 * policyholder is not asked for: a member inherits the master's (D7), and the tariff's rule says
 * which other parties it needs. The policy number is minted by the service; nothing is invented here.
 *
 * A refusal for the partners arrives at `/partners/<i>/…` and lands on the row it names, or on the
 * list when a required role is unfilled — by the tariff's own rule, by name.
 */
export function CreatePolicyDialog({
  parentPolicyCode,
  allowedTarifs,
  memberSchema,
  open,
  onClose,
}: {
  readonly parentPolicyCode: string;
  readonly allowedTarifs: readonly string[];
  readonly memberSchema: {
    readonly data?: InstanceApi.GetGroupPolicyMemberSchemaResponse;
    readonly isPending: boolean;
    readonly isError: boolean;
  };
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const create = useCreatePolicy();
  const rules = useMemo(
    () => editableRoles(memberSchema.data?.partnerRoles, true),
    [memberSchema.data],
  );
  const form = useMemo(() => payloadFormOf(memberSchema.data?.schema), [memberSchema.data?.schema]);
  const validator = useMemo(() => payloadSchemaOf(form), [form]);

  const [tarifErn, setTarifErn] = useState(allowedTarifs[0] ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [partners, setPartners] = useState<PartnerFormValues>({});
  const [seeded, setSeeded] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!seeded && memberSchema.data !== undefined) {
    setPartners(seedPartnerValues(rules, []));
    setSeeded(true);
  }

  const parsed = validator.safeParse(coercePayload(form, values));
  const fieldErrors: Record<string, string> = {};
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const name = fieldNameOfIssuePath(issue.path);
      if (name !== '' && fieldErrors[name] === undefined) {
        fieldErrors[name] = t(`policies.endorse.errors.${issue.message}` as never, {
          defaultValue: t('policies.endorse.errors.invalid'),
        });
      }
    }
  }
  const dateMissing = effectiveFrom === '';
  const refusal = partnerRefusalOf(create.error, rules, partners);

  const close = (): void => {
    setSubmitted(false);
    setSeeded(false);
    setEffectiveFrom('');
    setValues({});
    setPartners({});
    create.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{t('policies.create.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('policies.create.description')}
          </Typography>

          <TextField
            select
            label={t('policies.create.tarif')}
            value={tarifErn}
            required
            fullWidth
            onChange={(event) => {
              setTarifErn(event.target.value);
            }}
          >
            {allowedTarifs.map((ern) => (
              <MenuItem key={ern} value={ern}>
                {ern}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            label={t('policies.create.effectiveFrom')}
            value={effectiveFrom}
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
            }}
            error={submitted && dateMissing}
            helperText={
              submitted && dateMissing
                ? t('policies.endorse.errors.required')
                : t('policies.create.effectiveFromHint')
            }
          />

          {memberSchema.isPending ? (
            <Skeleton variant="rounded" height={200} />
          ) : memberSchema.isError ? (
            <Alert severity="error">{t('policies.create.schemaError')}</Alert>
          ) : (
            <>
              <PartnerSection
                rules={rules}
                values={partners}
                member
                refusal={refusal}
                onChange={(roleErn, ids) => {
                  setPartners((current) => ({ ...current, [roleErn]: ids }));
                }}
              />
              <PayloadFields
                form={form}
                values={values}
                errors={submitted ? fieldErrors : {}}
                onChange={(name, value) => {
                  setValues((current) => ({ ...current, [name]: value }));
                }}
              />
            </>
          )}

          {create.isSuccess && (
            <Alert severity="success" data-testid="policy-created">
              {t('policies.create.created', { code: create.data.policy?.code ?? '' })}
            </Alert>
          )}
          {create.isError && !hasPartnerRefusal(refusal) && (
            <Alert severity="error">{create.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('policies.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={create.isPending || create.isSuccess || memberSchema.isPending}
          onClick={() => {
            setSubmitted(true);
            if (dateMissing || !parsed.success) return;
            create.mutate({
              parentPolicyCode,
              tarifErn,
              effectiveFrom,
              data: parsed.data,
              partners: linksOf(rules, partners),
            });
          }}
        >
          {t('policies.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
